import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import Hero from '../../components/Hero';
import FormatDate from '../../components/FormatDate';
import Footer from '../../components/Footer';

import './index.css';

export default function News() {
    const limitRef = useRef(null);

    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fromQuery, setFromQuery] = useState(0);
    const [toQuery, setToQuery] = useState(6);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        if (limitRef.current && hasMore && !loading) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setFromQuery(prevFrom => prevFrom + 6);
                        setToQuery(prevTo => prevTo + 6);
                    }
                });
            }, {
                threshold: 0.1
            });

            observer.observe(limitRef.current);

            return () => {
                if (limitRef.current) {
                    observer.unobserve(limitRef.current);
                }
            };
        }
    }, [limitRef.current, hasMore, loading]);

    useEffect(() => {
        if (!hasMore && news.length > 0) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const apiUrl = `/api/news/?from=${fromQuery}&to=${toQuery}`;

        const fetchData = async () => {
            try {
                const response = await fetch(apiUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const jsonData = await response.json();

                if (jsonData.length === 0 || jsonData.length < (toQuery - fromQuery + 1)) {
                    setHasMore(false);
                }

                // --- MODIFICACIÓN PARA EVITAR CLAVES DUPLICADAS ---
                setNews(prevNews => {
                    // Crea un 'Set' con todos los 'uuid_news' de las noticias que ya tienes en el estado.
                    const existingUuids = new Set(prevNews.map(item => item.uuid_news));
                    // Filtra los 'jsonData' (nuevas noticias) para incluir solo aquellas cuyo 'uuid_news'
                    // no se encuentre ya en nuestro 'Set' de IDs existentes.
                    const uniqueNewData = jsonData.filter(item => !existingUuids.has(item.uuid_news));
                    // Devuelve un nuevo arreglo combinando las noticias anteriores con las nuevas noticias únicas.
                    return [...prevNews, ...uniqueNewData];
                });
                // -------------------------------------------------

                setLoading(false);
            } catch (e) {
                setError(e);
                setLoading(false);
            }
        };

        fetchData();
    }, [fromQuery, toQuery]);

    if (loading && news.length === 0 && hasMore) {
        return <Loader topAvance={150} />;
    }
    if (error) {
        return <p>Error al cargar las noticias: {error.message}</p>;
    }

    const charLimit = 200;

    return (
        <main className="g-main news">
            <Hero title="Noticias" description="Últimas noticias de la Iglesia" size='s' />
            <section className="content-main">
                {news.map((item) => (
                    <div key={item.uuid_news} className="container-news">
                        <span className="title-news">{item.title}</span>
                        <span className="date-news"><FormatDate date={item.date} format="xl" /></span>
                        <p className="text-news">
                            {item.news.length <= charLimit ? item.news : item.news.substring(0, charLimit) + '...'}
                            <span className="read-more">
                                <Link to={`/noticia/${item.uuid_news}`} className="lnk-read-more-news">
                                    Leer más
                                </Link>
                            </span>
                        </p>
                    </div>
                ))}

                {hasMore && (
                    <aside className="limit-viewport" ref={limitRef}>
                        {loading ? <Loader /> : <p className="end-news">Deslice para cargar más noticias</p>}
                    </aside>
                )}

                {!hasMore && news.length > 0 && (
                    <p className="end-news">No hay más noticias para mostrar.</p>
                )}
                {!hasMore && news.length === 0 && !loading && (
                    <p className="end-news">Aún no hay más noticias para mostrar.</p>
                )}
            </section>
            <Footer />
        </main>
    );
}